import {
  LatestCloseValue,
  PositionSnapshot,
  StrategyLegSnapshot,
  StrategyPositionSnapshot,
  TickL1,
} from '../../types/trading';

export const normalizeSymbol = (symbol: string | null | undefined): string => {
  return symbol?.trim().toUpperCase().replace(/^O:/, '') ?? '';
};

const asFiniteNumber = (value: unknown): number | null => {
  return typeof value === 'number' && !Number.isNaN(value) ? value : null;
};

export interface PositionRowMetricsInput {
  position: PositionSnapshot;
  resolvedLast: number | null;
  previousClose: number | null;
  marketIsOpen: boolean | null;
  forceLastEqualsPreviousWhenClosed?: boolean;
}

export interface PositionRowMetrics {
  last: number | null;
  previous: number | null;
  totalCost: number | null;
  marketValue: number | null;
  pnl: number | null;
  dailyReturnPct: number | null;
  totalReturnPct: number | null;
}

export const calculatePositionRowMetrics = ({
  position,
  resolvedLast,
  previousClose,
  marketIsOpen,
  forceLastEqualsPreviousWhenClosed = false,
}: PositionRowMetricsInput): PositionRowMetrics => {
  const netQty = asFiniteNumber(position.net_qty) ?? 0;
  const absQty = Math.abs(netQty);
  const avgPrice = asFiniteNumber(position.avg_price);
  const sanitizedPrevious = asFiniteNumber(previousClose);
  const sanitizedResolvedLast = asFiniteNumber(resolvedLast);

  const displayLast = (
    forceLastEqualsPreviousWhenClosed
    && marketIsOpen === false
    && sanitizedPrevious != null
  )
    ? sanitizedPrevious
    : sanitizedResolvedLast;

  const totalCost = avgPrice != null ? avgPrice * absQty : null;
  const marketValue = displayLast != null ? displayLast * absQty : null;

  const pnlFromMark = (
    displayLast != null
    && avgPrice != null
  )
    ? (displayLast - avgPrice) * netQty
    : null;
  const fallbackPnl = asFiniteNumber(position.unrealized_pnl);
  const pnl = pnlFromMark ?? fallbackPnl;

  const dailyReturnPct = (
    totalCost != null
    && totalCost !== 0
    && displayLast != null
    && sanitizedPrevious != null
  )
    ? (((displayLast - sanitizedPrevious) * netQty) / totalCost) * 100
    : null;

  const totalReturnPct = (
    totalCost != null
    && totalCost !== 0
    && pnl != null
  )
    ? (pnl / totalCost) * 100
    : null;

  return {
    last: displayLast,
    previous: sanitizedPrevious,
    totalCost,
    marketValue,
    pnl,
    dailyReturnPct,
    totalReturnPct,
  };
};

export const isActiveStrategyLeg = (leg: StrategyLegSnapshot): boolean => {
  const netQty = asFiniteNumber(leg.net_qty);
  return netQty != null && netQty !== 0;
};

export const resolvePositionMarkPrice = (
  position: PositionSnapshot,
  ticksBySymbol: Record<string, TickL1 | undefined>,
  latestCloseBySymbol: Record<string, LatestCloseValue | undefined> = {},
  marketIsOpen: boolean | null = null,
): number | null => {
  const symbol = normalizeSymbol(position.symbol) || position.symbol;
  const tick = ticksBySymbol[symbol];
  const tickBid = asFiniteNumber(tick?.bid);
  const tickAsk = asFiniteNumber(tick?.ask);
  const liveMid =
    tickBid != null && tickAsk != null && tickBid > 0 && tickAsk > 0
      ? (tickBid + tickAsk) / 2
      : null;
  const liveLast = asFiniteNumber(tick?.last);

  const latestCloseValue = latestCloseBySymbol[symbol];
  const latestClose =
    latestCloseValue?.status === 'ok' ? asFiniteNumber(latestCloseValue.latest_close) : null;

  if (marketIsOpen === false) {
    if (latestClose != null) return latestClose;
    return asFiniteNumber(position.last_price);
  }

  if (liveMid != null) return liveMid;
  if (liveLast != null) return liveLast;
  if (latestClose != null) return latestClose;

  return asFiniteNumber(position.last_price);
};

export const resolveStrategyLegMarkPrice = (
  leg: StrategyLegSnapshot,
  ticksBySymbol: Record<string, TickL1 | undefined>,
): number | null => {
  const symbol = normalizeSymbol(leg.symbol);
  if (symbol) {
    const tick = ticksBySymbol[symbol];
    const tickBid = asFiniteNumber(tick?.bid);
    const tickAsk = asFiniteNumber(tick?.ask);
    const liveMid =
      tickBid != null && tickAsk != null && tickBid > 0 && tickAsk > 0
        ? (tickBid + tickAsk) / 2
        : null;
    const liveLast = asFiniteNumber(tick?.last);
    if (liveMid != null) return liveMid;
    if (liveLast != null) return liveLast;
  }

  return asFiniteNumber(leg.last_price);
};

export const calculateStrategyLegPnl = (
  leg: StrategyLegSnapshot,
  ticksBySymbol: Record<string, TickL1 | undefined>,
): number | null => {
  const mark = resolveStrategyLegMarkPrice(leg, ticksBySymbol);
  const avgPrice = asFiniteNumber(leg.avg_price);
  const netQty = asFiniteNumber(leg.net_qty);

  if (mark != null && avgPrice != null && netQty != null) {
    return (mark - avgPrice) * netQty;
  }

  return asFiniteNumber(leg.pnl);
};

export const collectPortfolioSymbolsForLiveTicks = (
  positions: PositionSnapshot[],
  strategyPositions: StrategyPositionSnapshot[],
): string[] => {
  const symbols = new Set<string>();

  positions.forEach((position) => {
    const symbol = normalizeSymbol(position.symbol);
    if (symbol) symbols.add(symbol);
  });

  strategyPositions.forEach((strategyPosition) => {
    strategyPosition.legs.forEach((leg) => {
      if (!isActiveStrategyLeg(leg)) return;
      const symbol = normalizeSymbol(leg.symbol);
      if (symbol) symbols.add(symbol);
    });
  });

  return Array.from(symbols);
};

export const calculatePortfolioTotalValue = (
  cash: number,
  positions: PositionSnapshot[],
  ticksBySymbol: Record<string, TickL1 | undefined>,
  strategyPositions: StrategyPositionSnapshot[] = [],
  latestCloseBySymbol: Record<string, LatestCloseValue | undefined> = {},
  marketIsOpen: boolean | null = null,
): number | null => {
  let total = cash;

  for (const position of positions) {
    if (position.net_qty === 0) continue;
    const markPrice = resolvePositionMarkPrice(
      position,
      ticksBySymbol,
      latestCloseBySymbol,
      marketIsOpen,
    );
    if (markPrice == null) return null;
    total += position.net_qty * markPrice;
  }

  for (const strategyPosition of strategyPositions) {
    for (const leg of strategyPosition.legs) {
      const netQty = asFiniteNumber(leg.net_qty);
      if (netQty == null || netQty === 0) continue;
      const markPrice = resolveStrategyLegMarkPrice(leg, ticksBySymbol);
      if (markPrice == null) return null;
      total += netQty * markPrice;
    }
  }

  return total;
};

export const formatCurrencyValue = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '--';
  const abs = Math.abs(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return value < 0 ? `-$${abs}` : `$${abs}`;
};
